import { SandpackCodeEditor, SandpackProvider } from '@codesandbox/sandpack-react'
import { Flatfile } from '@flatfile/api-internal'
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Icon,
  IconName,
  Spinner,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@flatfile/baseline'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../api/http-service'
import { useActiveEnvironment } from '../../hooks/useEnvironments'
import { useIdentity } from '../../hooks/useIdentity'
import { useCreateResource, useUpdateResource } from '../../resources/hooks/useResourceMutation'
import getCollection from '../../resources/support/getCollection'
import { logger } from '../../utils/logging'
import { spacesUrl } from '../../utils/pageUtils'
import { eventList } from '../Agents/AgentUtils'

/* eslint-disable no-useless-escape */
export const demos = [
  {
    heading: 'Extractors',
    description:
      'Effortlessly handle and manipulate data, irrespective of its source or format. Whether arriving as CSV, PDF, XML, or various other types, data can seamlessly be extracted to coexist within your Workbook.',
    tsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/typescript/extractors/index.ts',
    jsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/javascript/extractors/index.js',
    icon: 'archive',
    namespace: 'extractor-demo',
    filename: 'extractors.js',
    file: {
      'extractors.js': {
        code: `import { FlatfileListener } from "@flatfile/listener"
import { JSONExtractor } from "@flatfile/plugin-json-extractor"

export default function flatfileEventListener(listener: FlatfileListener) {
  listener.use(JSONExtractor())
}`,
        hidden: true,
        readOnly: true,
      },
    },
  },
  {
    heading: 'Data Handling',
    description:
      'Data Hooks are concise functions that automatically re-format, correct, validate, and enrich data during the data import process. These hooks can be executed on a record (row) using your own custom logic.',
    tsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/typescript/handling-data/index.ts',
    jsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/javascript/handling-data/index.js',
    icon: 'magic-wand-2',
    namespace: 'data-demo',
    filename: 'dataHandling.js',
    file: {
      'dataHandling.js': {
        code: `import { FlatfileListener } from "@flatfile/listener"
import { recordHook, FlatfileRecord } from "@flatfile/plugin-record-hook"

export default function flatfileEventListener(listener: FlatfileListener) {
  listener.use(
    recordHook("contacts", (record: FlatfileRecord) => {
      record.compute(
        "email",
        (email, record) =>
          record.get('first_name')?.toString().toLowerCase() 
          + record.get('last_name').toString().toLowerCase() 
          + '@acme.com',
        "Email was generated from first and last name."
      )

      record.computeIfPresent(
        "email",
        (email) => email?.toString() || "".toLowerCase(),
        "Email was converted to lowercase."
      )

      record.validate(
        "last_name",
        (value) => typeof value === "string" && !/\d/.test(value),
        "Last name cannot contain numbers."
      )
      return record
    })
  )
}`,
        hidden: true,
        readOnly: true,
      },
    },
  },
  {
    heading: 'Dynamic Configurations',
    description:
      'Spaces are micro-applications, each has its own database, filestore, and auth. Learn how to dynamically configure a Space from scratch, and update it as things change.',
    tsLink:
      'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/typescript/dynamic-configurations/index.ts',
    jsLink:
      'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/javascript/dynamic-configurations/index.js',
    icon: 'focus-flash',
    namespace: 'dynamic-demo',
    filename: 'dynamic.js',
    file: {
      'dynamic.js': {
        code: `import api from "@flatfile/api"
import { Client, FlatfileEvent, FlatfileListener } from "@flatfile/listener"

export default function flatfileEventListener(listener: Client) {
  listener.filter({ job: "space:configure" }, (configure: FlatfileListener) => {
    configure.on(
      "job:ready",
      async ({ context: { spaceId, environmentId, jobId } }: FlatfileEvent) => {
        try {
          await api.jobs.ack(jobId, {
            info: "Job started.",
            progress: 10,
          })

          await api.workbooks.create({
            spaceId,
            environmentId,
            ... // workbook definition
          })

          await api.jobs.complete(jobId, {
            outcome: {
              message: "Job completed.",
            }
          })
        } catch (error: any) {
          await api.jobs.fail(jobId, {
            outcome: {
              message: "Error: " + error
            }
          })
        }
      }
    )
  })
}`,
        hidden: true,
        readOnly: true,
      },
    },
  },
  {
    heading: 'Using Actions',
    description:
      'Tailor your workflow to integrate with and optimize your existing processes with Actions. Actions are custom code-based operations that can be configured wherever that Action is mounted: Space, Workbook, or Sheet.',
    tsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/tree/main/typescript/actions',
    jsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/tree/main/javascript/actions',
    icon: 'lightning',
    namespace: 'actions-demo',
    filename: 'actions.js',
    file: {
      'actions.js': {
        code: `import api from "@flatfile/api"
import { FlatfileEvent, FlatfileListener } from "@flatfile/listener"

export default function flatfileEventListener(listener: FlatfileListener) {
  listener.filter({ job: "workbook:submitActionFg" }, (configure) => {
    configure.on("job:ready", async (event: FlatfileEvent) => {
      const { jobId } = event.context;
      try {
        await api.jobs.ack(jobId, {
          info: "Job started.",
          progress: 10,
        })

        await api.jobs.complete(jobId, {
          outcome: {
            message: \`Job "Custom Action" completed.\`,
          }
        })
      } catch (error: any) {        
        await api.jobs.fail(jobId, {
          outcome: {
            message: "Job encountered an error.",
          }
        })
      }
    })
  })
}`,
        hidden: true,
        readOnly: true,
      },
    },
  },
  {
    heading: 'Localization',
    description:
      'Localization enables you to tailor the language and text in your Flatfile Space. Learn how to translate and customize your Space with text overrides.',
    tsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/typescript/localization/index.ts',
    jsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/javascript/localization/index.js',
    icon: 'translate',
    namespace: 'localization-demo',
    filename: 'localization.js',
    file: {
      'localization.js': {
        code: `import api from "@flatfile/api"
import { Client, FlatfileEvent, FlatfileListener } from "@flatfile/listener"

export default function flatfileEventListener(listener: Client) {
  listener.filter({ job: "space:configure" }, (configure: FlatfileListener) => {
    configure.on(
      "job:ready",
      async ({ context: { spaceId, jobId } }: FlatfileEvent) => {
        try {
          await api.jobs.ack(jobId, {
            info: "Job started.",
            progress: 10,
          })

          await api.spaces.update(spaceId, {
            translationsPath:
              "https://raw.githubusercontent.com/FlatFilers/Platform-Translations/demo-structure/locales/en/translation.json",
          })

          await api.jobs.complete(jobId, {
            outcome: {
              message: "Job completed.",
            }
          })
        } catch (error: any) {
          await api.jobs.fail(jobId, {
            outcome: {
              message: "Error: " + error
            }
          })
        }
      }
    )
  })
}`,
        hidden: true,
        readOnly: true,
      },
    },
  },
  {
    heading: 'Secrets',
    description:
      'Secrets are securely stored key-value pairs associated with either an Environment or a Space. Use Secrets to store any credentials that your Flatfile implementation might need to work with your data or integrate with other systems.',
    tsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/typescript/secrets/index.ts',
    jsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/javascript/secrets/index.js',
    icon: 'key-2',
    namespace: 'secrets-demo',
    filename: 'secrets.js',
    file: {
      'secrets.js': {
        code: `import api from "@flatfile/api"
import { Client, FlatfileEvent, FlatfileListener } from "@flatfile/listener"

export default function flatfileEventListener(listener: Client) {
  listener.filter({ job: "space:configure" }, (configure: FlatfileListener) => {
    configure.on(
      "job:ready",
      async ({ context: { spaceId, environmentId, jobId } }: FlatfileEvent) => {
        try {
          await api.jobs.ack(jobId, {
            info: "Job started.",
            progress: 10,
          })

          await api.secrets.upsert({
            environmentId,
            spaceId,
            name: "My First Secret",
            value: "My Super Secret Value",
          })

          await api.jobs.complete(jobId, {
            outcome: {
              message: "Job completed.",
            }
          })
        } catch (error: any) {
          await api.jobs.fail(jobId, {
            outcome: {
              message: "Error: " + error
            }
          })
        }
      }
    )
  })
}`,
        hidden: true,
        readOnly: true,
      },
    },
  },
  {
    heading: 'Namespaces',
    description:
      'Namespaces are a way to group your Spaces in an organizational schema that makes sense for your business. Narrow the scope of your operations by matching on a Namespace.',
    tsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/typescript/namespaces/index.ts',
    jsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/javascript/namespaces/index.js',
    icon: 'bezier-curve',
    namespace: 'namespace-demo',
    filename: 'namespaces.js',
    file: {
      'namespaces.js': {
        code: `import { Client } from "@flatfile/listener"
import red from "./red"
import green from "./green"

export default function (listener: Client) {
  listener.namespace(["space:red"], red)
  listener.namespace(["space:green"], green)
}`,
        hidden: true,
        readOnly: true,
      },
    },
  },
  {
    heading: 'Metadata',
    description:
      'Contextualize your data by storing and retrieving additional information about an Environment, Space, Record, or Field without exposing it to end users.',
    tsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/typescript/metadata/index.ts',
    jsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/javascript/metadata/index.js',
    icon: 'file-text',
    namespace: 'metadata-demo',
    filename: 'metadata.js',
    file: {
      'metadata.js': {
        code: `import api from "@flatfile/api"
import { Client, FlatfileEvent, FlatfileListener } from "@flatfile/listener"

export default function flatfileEventListener(listener: Client) {
  listener.filter({ job: "space:configure" }, (configure: FlatfileListener) => {
    configure.on(
      "job:ready",
      async ({ context: { spaceId, environmentId, jobId } }: FlatfileEvent) => {
        try {
          await api.jobs.ack(jobId, {
            info: "Job started.",
            progress: 10,
          })
  
          await api.spaces.update(spaceId, {
            metadata: {
              // custom metatdata key-value pairs
            }
          })

          await api.jobs.complete(jobId, {
            outcome: {
              message: "Job completed.",
            }
          })
        } catch (error: any) {
          await api.jobs.fail(jobId, {
            outcome: {
              message: "Error: " + error
            }
          })
        }
      }
    )
  })
}`,
        hidden: true,
        readOnly: true,
      },
    },
  },
  {
    heading: 'Egress',
    description:
      'After importing, validating, and transforming it, your data now needs to be integrated into another system. Learn how to forward your data to its destination.',
    tsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/typescript/egress/index.ts',
    jsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/javascript/egress/index.js',
    icon: 'arrow-out-of-box',
    namespace: 'egress-demo',
    filename: 'egress.js',
    file: {
      'egress.js': {
        code: `import api from "@flatfile/api"
import { Client, FlatfileEvent } from "@flatfile/listener"

export default function flatfileEventListener(listener: Client) {
  listener.filter({ job: "workbook:submitActionFg" }, (configure) => {
    configure.on("job:ready", async (event: FlatfileEvent) => {
      const { jobId } = event.context
      try {
        await api.jobs.ack(jobId, {
          info: "Job started.",
          progress: 10,
        })

        // Custom egress logic, e.g. sending data to a third-party API

        await api.jobs.complete(jobId, {
          outcome: {
            message: \`Job "Send Workbook To ACME" completed.\`,
          }
        })

      } catch (error: any) {
        await api.jobs.fail(jobId, {
          outcome: {
            message: "Error: " + error
          }
        })
      }
    })
  })
}`,
        hidden: true,
        readOnly: true,
      },
    },
  },
  {
    heading: 'Headless',
    description:
      'Use Flatfile to deliver an adaptable but completely automated, behind-the-scenes, data experience, leveraging a human in the middle only when necessary.',
    tsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/tree/main/typescript/headless',
    jsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/tree/main/javascript/headless',
    icon: 'settings-gear-2',
    namespace: 'headless-demo',
    filename: 'headless.js',
    file: {
      'headless.js': {
        code: `import api from "@flatfile/api"
import { Client, FlatfileEvent, FlatfileListener } from "@flatfile/listener"
import { ExcelExtractor } from "@flatfile/plugin-xlsx-extractor"
import { automap } from "@flatfile/plugin-automap"
import { recordHook } from "@flatfile/plugin-record-hook"

export default function flatfileEventListener(listener: Client) {
  listener.filter({ job: "space:configure" }, (configure: FlatfileListener) => {
    configure.on(
      "job:ready",
      async ({ context: { spaceId, environmentId, jobId } }: FlatfileEvent) => {
        try {
          await api.jobs.ack(jobId, {
            info: "Job started.",
            progress: 10,
          })

          await api.workbooks.create({
            spaceId,
            environmentId,
            ... // workbook configuration
          })

          await api.jobs.complete(jobId, {
            outcome: {
              message: "Job completed.",
            }
          })
        } catch (error: any) {

          await api.jobs.fail(jobId, {
            outcome: {
              message: "Error: " + error
            }
          })
        }
      }
    )
  })

  listener.use(ExcelExtractor({ rawNumbers: true }))
  listener.use(
    automap({
      accuracy: "confident",
      defaultTargetSheet: "PrimarySheet",
      matchFilename: /^.*primarySheet\.xlsx$/,
      onFailure: console.error,
    })
  )

  listener.use(
    recordHook("inventory", async (record, event) => {
      const author = record.get("author");
      function validateNameFormat(name: string) {
        const pattern: RegExp = /^\s*[\p{L}'-]+\s*,\s*[\p{L}'-]+\s*$/u;
        return pattern.test(name)
      }

      if (!validateNameFormat(author as string)) {
        const nameSplit = (author as string).split(" ");
        record.set("author", nameSplit[1] + ',' +nameSplit[0])
        record.addComment("author", "Author name was updated for vendor")
        return record 
      }
    })
  )

  listener.on("commit:completed", async (event: FlatfileEvent) => {
    const { jobId } = event.context;
    try {
      await api.jobs.ack(jobId, {
        info: "Job started.",
        progress: 10,
      })

      // Custom egress logic, e.g. sending data to a third-party API

      await api.jobs.complete(jobId, {
        outcome: {
          message: 'Job "Send Workbook To ACME" completed.',
        }
      })
    } catch (error: any) {
      await api.jobs.fail(jobId, {
        outcome: {
          message: "Job encountered an error.",
        }
      })
    }
  })
}`,
        hidden: true,
        readOnly: true,
      },
    },
  },
  {
    heading: 'Documents',
    description:
      'Documents are standalone webpages that reside in your Flatfile Space. Documents are extremely useful and can be updated with dynamically generated content as Events occur.',
    tsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/typescript/documents/index.ts',
    jsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/javascript/documents/index.js',
    icon: 'code',
    namespace: 'documents-demo',
    filename: 'documents.js',
    file: {
      'documents.js': {
        code: `import api from "@flatfile/api"
import { Client, FlatfileEvent, FlatfileListener } from "@flatfile/listener"
import { myDocument } from "../constants/documents.json"

export default function flatfileEventListener(listener: Client) {
  listener.filter({ job: "space:configure" }, (configure: FlatfileListener) => {
    configure.on(
      "job:ready",
      async ({ context: { spaceId, environmentId, jobId } }: FlatfileEvent) => {
        try {
          await api.jobs.ack(jobId, {
            info: "Job started.",
            progress: 10,
          })

          const { data } = await api.documents.create(spaceId, {
            title: "About this Documents Demo",
            body: myDocument,
          })

          const documentId = data.id;

          await api.spaces.update(spaceId, {
            metadata: {
              sidebarConfig: {
                defaultPage: {
                  documentId,
                }
              }
            }
          })

        } catch (error: any) {
          await api.jobs.fail(jobId, {
            outcome: {
              message: "Error: " + error
            }
          })
        }
      }
    )
  })
}`,
        hidden: true,
        readOnly: true,
      },
    },
  },
  {
    heading: 'Sidebar',
    description:
      'Flatfile allows you to update your sidebar to hide and show certain elements. Learn how to create a sidebar configuration, configuring it dynamically for different user experiences.',
    tsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/typescript/guest-sidebar/index.ts',
    jsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/javascript/guest-sidebar/index.js',
    icon: 'sidebar',
    namespace: 'sidebar-demo',
    filename: 'sidebar.js',
    file: {
      'sidebar.js': {
        code: `import api from "@flatfile/api"
import { Client, FlatfileEvent, FlatfileListener } from "@flatfile/listener"

export default function flatfileEventListener(listener: Client) {
  listener.filter({ job: "space:configure" }, (configure: FlatfileListener) => {
    configure.on(
      "job:ready",
      async ({ context: { spaceId, jobId } }: FlatfileEvent) => {
        try {
          await api.jobs.ack(jobId, {
            info: "Job started.",
            progress: 10,
          })

          await api.spaces.update(spaceId, {
            metadata: {
              sidebarConfig: {
                showSidebar: false,
              }
            }
          })

          await api.jobs.complete(jobId, {
            outcome: {
              message: "Job completed.",
            }
          })
        } catch (error: any) {
          await api.jobs.fail(jobId, {
            outcome: {
              message: "Error: " + error
            }
          })
        }
      }
    )
  })
}`,
        hidden: true,
        readOnly: true,
      },
    },
  },
  {
    heading: 'Theming',
    description:
      'Tailor the visual identity of Flatfile to harmonize with your brand aesthetics. Our smart theming allows you to transform your space with a single API call.',
    tsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/typescript/theming/index.ts',
    jsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/javascript/theming/index.js',
    icon: 'color-palette',
    namespace: 'theming-demo',
    filename: 'theming.js',
    file: {
      'theming.js': {
        code: `import api from "@flatfile/api"
import { Client, FlatfileEvent, FlatfileListener } from "@flatfile/listener"

export default function flatfileEventListener(listener: Client) {
  listener.filter({ job: "space:configure" }, (configure: FlatfileListener) => {
    configure.on(
      "job:ready",
      async ({ context: { spaceId, jobId } }: FlatfileEvent) => {
        try {
          await api.jobs.ack(jobId, {
            info: "Job started.",
            progress: 10,
          })

          await api.spaces.update(spaceId, {
            metadata: {
              theme: {
                sidebar: {
                  // custom theme config
                }
              }
            }
          })

          await api.jobs.complete(jobId, {
            outcome: {
              message: "Job completed.",
            }
          })
        } catch (error: any) {
          await api.jobs.fail(jobId, {
            outcome: {
              message: "Error: " + error
            }
          })
        }
      }
    )
  })
}`,
        hidden: true,
        readOnly: true,
      },
    },
  },
  {
    heading: 'Merge.dev',
    description:
      'This is an alpha release connect plugin for Merge.dev. Using this plugin will enable you to sync data from hundreds of integrations (connected through Merge.dev) with Flatfile.',
    tsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/typescript/connect-alpha/index.ts',
    jsLink: 'https://github.com/FlatFilers/flatfile-docs-kitchen-sink/blob/main/javascript/connect-alpha/index.js',
    icon: 'merge',
    namespace: 'connect-demo',
    filename: 'merge.js',
    file: {
      'merge.js': {
        code: `import {  FlatfileListener } from "@flatfile/listener"
import mergePlugin from "@flatfile/plugin-connect-via-merge"

export default function flatfileEventListener(listener: FlatfileListener) {
  listener.use(mergePlugin())
}`,
        hidden: true,
        readOnly: true,
      },
    },
  },
]
/* eslint-enable no-useless-escape */

const files = demos.reduce((acc, item) => {
  Object.assign(acc, item.file)
  return acc
}, {})

// TimelineStep component from ProjectsHello.page.tsx
interface TimelineStepProps {
  isComplete: boolean
  stepNumber: number
  icon: IconName
  title: string
  children: React.ReactNode
  buttons?: React.ReactNode
}

const TimelineStep = ({ isComplete, stepNumber, icon, title, children, buttons }: TimelineStepProps) => {
  return (
    <div className='relative mb-8'>
      <div className='flex'>
        <div
          className={`flex items-center justify-center w-8 -ml-2 mr-2 flex-shrink-0 self-start h-8 ${isComplete ? 'bg-primary text-white' : 'bg-transparent text-primary'}`}
        >
          {isComplete ? (
            <Icon name='checkmark-2' size={18} />
          ) : icon ? (
            <Icon name={icon} size={18} />
          ) : (
            <span className='flex items-center justify-center text-lg -mt-1 leading-none'>{stepNumber}</span>
          )}
        </div>
        <div className='flex-1'>
          <Text variant='title3' emphasized className='mb-2'>
            {title}
          </Text>
          {children}
        </div>
      </div>
      {buttons && <div className='w-full flex justify-end gap-2 mt-6 ml-6'>{buttons}</div>}
    </div>
  )
}

// Replaced styled component with functional component using Tailwind classes
const IconCard = ({
  isActive,
  isDisabled,
  onClick,
  children,
}: {
  isActive?: boolean
  isDisabled?: boolean
  onClick?: () => void
  children: React.ReactNode
}) => {
  return (
    <div
      className={cn(
        'p-1.5 flex justify-center rounded-none transition-shadow border',
        isActive
          ? 'bg-overlay-darken-300 text-white border-white'
          : isDisabled
            ? 'border-border text-interactive-state-selected-pressed'
            : 'border-black text-primary',
        !isDisabled && 'cursor-pointer'
      )}
      onClick={!isDisabled ? onClick : undefined}
      onKeyDown={
        !isDisabled
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick?.()
              }
            }
          : undefined
      }
      role={!isDisabled ? 'button' : undefined}
      tabIndex={!isDisabled ? 0 : undefined}
    >
      {children}
    </div>
  )
}

const sandpackOpts = {
  activeFile: 'extractors.js',
  visibleFiles: ['extractors.js'],
}

export function CustomAppsHelloPage() {
  const { identity } = useIdentity()
  const [activeStep] = useState(0)
  const [example, setExample] = useState(demos[0])
  const [sandpackOptions, setSandpackOptions] = useState(sandpackOpts)
  const [createSpace] = useCreateResource('SPACES')
  const [createApp] = useCreateResource('APPS')
  const [createAgent] = useCreateResource('AGENTS')
  const [updateApp] = useUpdateResource('APPS')
  const [generated, setGenerated] = useState(false)
  const [currentApp, setCurrentApp] = useState({} as any)
  const { environment: activeEnvironment } = useActiveEnvironment()

  // Replace useDisclosure with useState
  const [openedDeployAgent, setOpenedDeployAgent] = useState(false)
  const openDeployAgent = () => setOpenedDeployAgent(true)
  const closeDeployAgent = () => setOpenedDeployAgent(false)

  const [deployingDemo, setDeployingDemo] = useState(false)
  const [slugs, setSlugs] = useState([] as any)
  const currentApps = getCollection('APPS').getItems()
  const unavailableExamples = currentApps.map((app: Flatfile.App) => app.namespace)

  const nav = useNavigate()

  useEffect(() => {
    if (activeStep === 0) {
      setGenerated(false)
    }
  }, [activeStep])

  useEffect(() => {
    if (generated) {
      setTimeout(() => {
        nav(`/apps/custom/${currentApp!.id}/list/`)
      }, 1000)
    }
  }, [generated])

  useEffect(() => {
    const fetchData = async () => {
      const { data } = await api.agents.list({ environmentId: activeEnvironment ? activeEnvironment.id : '' })
      setSlugs(data ? data.map((agent) => agent.slug) : [])
    }
    fetchData()
  }, [example])

  const generateDemo = async (demoNamespace: string, heading: string, env: Flatfile.Environment) => {
    try {
      const newApp = await createApp({
        name: heading,
        namespace: demoNamespace,
        type: 'CUSTOM',
        entity: 'Space',
        entityPlural: 'Spaces',
        environmentFilters: activeEnvironment.isProd ? { type: ['DEV', 'PROD'] } : { type: ['DEV'] },
      })
      getCollection('APPS').append(newApp)
      setCurrentApp(newApp)
      updateApp(newApp!.id, { activatedAt: new Date() })

      const newSpace = await createSpace({
        autoConfigure: true,
        name: heading,
        namespace: demoNamespace,
        labels: [demoNamespace],
        environmentId: env.id,
        appId: newApp.id,
      })

      const spaceRootUrl = spacesUrl(newSpace?.id ?? '', identity.token)
      window.open(spaceRootUrl, '_blank', 'noopener,noreferrer')
      setGenerated(true)
    } catch (e) {
      logger.error('Error generating demo', { error: e })
    }
  }

  const deployDemoAgentAndDemo = async (env: Flatfile.Environment) => {
    try {
      const response = await fetch('https://raw.githubusercontent.com/FlatFilers/demo-agent/main/dist/demo.js')
      const file = await response.text()

      setDeployingDemo(true)
      await createAgent({
        environmentId: env.id,
        agentConfig: {
          topics: eventList,
          compiler: 'js',
          source: file,
          slug: 'demo',
        },
      })
      setDeployingDemo(false)
      closeDeployAgent()
      generateDemo(example.namespace, example.heading, env)
    } catch (e) {
      logger.error('Error deploying demo agent', { error: e })
    }
  }

  return (
    activeEnvironment && (
      <div className='w-full h-full flex flex-col overflow-auto p-3'>
        <div className='grid grid-cols-12 gap-8'>
          <div className='col-span-5'>
            <div className='p-3'>
              <Text variant='title2'>Your use case, in Flatfile.</Text>
            </div>
            <div className='relative ml-1 flex flex-col'>
              <TimelineStep isComplete={false} stepNumber={1} icon='sparkles-soft' title='Start from a working example'>
                <div>
                  <Text variant='body' color='text-default' className='mt-2'>
                    Select one of our interactive demos to experience a custom app firsthand without writing a single
                    line of code.
                  </Text>
                  <div className='h-4'></div>
                  <div className='flex flex-wrap gap-2'>
                    {demos.map((demo) => (
                      <Tooltip key={demo.heading}>
                        <TooltipTrigger asChild>
                          <div>
                            <IconCard
                              isActive={
                                example.heading === demo.heading && !unavailableExamples.includes(demo.namespace)
                              }
                              isDisabled={unavailableExamples.includes(demo.namespace)}
                              onClick={() => {
                                setExample(demo)
                                setSandpackOptions({ activeFile: demo.filename, visibleFiles: [demo.filename] })
                              }}
                            >
                              <Icon name={demo.icon as IconName} size={16} />
                            </IconCard>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className='flex items-center px-2 py-1'>
                          <Text variant='body' className='text-sm'>
                            {unavailableExamples.includes(demo.namespace)
                              ? `${demo.heading} already created`
                              : demo.heading}
                          </Text>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                  <Text variant='footnote' className='pt-4'>
                    {example.description}
                  </Text>
                  <div className='h-4'></div>
                  {generated ? (
                    <div className='text-center'>
                      <div className='h-4'></div>
                      <Text>Creating {example.heading} example...</Text>
                      <div className='h-6'></div>
                      <Spinner />
                    </div>
                  ) : (
                    <>
                      <Text variant='body' className='pt-2'>
                        <span className='font-medium'>{example.heading}</span> <small>example selected.</small>
                        {unavailableExamples.includes(example.namespace) && <small> Choose another option.</small>}
                      </Text>

                      <div className='h-4'></div>
                      <>
                        <Dialog open={openedDeployAgent} onOpenChange={(open: boolean) => !open && closeDeployAgent()}>
                          <DialogContent>
                            <DialogTitle>{`Generate ${example.heading} Demo`}</DialogTitle>
                            <div className='relative'>
                              <div>
                                <Text variant='body' color='text-default'>
                                  {`Your current environment does not have a demo agent deployed. Would you like to proceed by deploying a demo agent to ${activeEnvironment.name}?`}
                                </Text>
                                <Button
                                  className='mt-4 mb-4 w-full'
                                  disabled={unavailableExamples.includes(example.namespace) || deployingDemo}
                                  onClick={() => {
                                    deployDemoAgentAndDemo(activeEnvironment)
                                  }}
                                  variant='outline'
                                  icon={deployingDemo ? 'loader' : undefined}
                                  iconProps={deployingDemo ? { className: 'animate-spin', size: 16 } : undefined}
                                >
                                  {deployingDemo ? 'Deploying...' : `Deploy demo agent to ${activeEnvironment.name}`}
                                </Button>
                                <Text variant='body' color='text-default'>
                                  Please Note: We do not recommend deploying a demo agent in a production environment.
                                </Text>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                        <div className='flex justify-start mt-4'>
                          <Button
                            onClick={() => {
                              slugs.includes('demo')
                                ? generateDemo(example.namespace, example.heading, activeEnvironment)
                                : openDeployAgent()
                            }}
                            disabled={deployingDemo}
                            icon={deployingDemo ? 'loader' : undefined}
                            iconProps={deployingDemo ? { className: 'animate-spin', size: 16 } : undefined}
                          >{`${deployingDemo ? 'Creating' : 'Create'} ${
                            example.heading && example.heading.length ? example.heading.toLowerCase() : 'extractors'
                          } example`}</Button>
                        </div>
                      </>
                    </>
                  )}
                </div>
              </TimelineStep>
            </div>
          </div>
          <div className='col-span-7'>
            <SandpackProvider
              theme={{
                colors: {
                  surface1: '#ffffff',
                  surface2: 'var(--color-gray-4)',
                  surface3: '#efeff3',
                  clickable: '#000',
                  disabled: '#C5C5C5',
                  hover: '#4D4D4D',
                  accent: '#3B2FC9',
                  error: '#ff453a',
                  errorSurface: '#ffeceb',
                },
                font: {
                  body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
                  mono: "'PT Mono', 'DejaVu Sans Mono', Menlo, Consolas, 'Liberation Mono', Monaco, 'Lucida Console', monospace",
                  size: '13px',
                  lineHeight: '20px',
                },
              }}
              files={files}
              options={sandpackOptions}
              style={{ border: '1px solid var(--color-gray-4)' }}
              key={example.heading}
            >
              <SandpackCodeEditor showRunButton={false} showTabs style={{ height: 'calc(100vh - 6rem)' }} />
            </SandpackProvider>
          </div>
        </div>
      </div>
    )
  )
}
